import React from 'react';
import { Grid, Card, Typography, Space, Row, Col } from 'antd';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
  Legend
} from 'recharts';
import { formatDate } from '../../utils/dateFormatters';
import { filterByNits } from '../../utils/inventoryReports';

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

/**
 * StockoutForecastChart - Predictive chart for potential stockouts (Responsive)
 *
 * Pronostico de productos en riesgo de agotarse.
 * Basado en la tendencia de los ultimos dias, estima cuantos productos
 * podrian quedar sin stock en los proximos 5 dias.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 * @param {string[]} props.nitList - Selected NITs for filtering
 */
function StockoutForecastChart({ data, nitList }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;

  // Transform data and calculate forecast
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const relevantData = filterByNits(data, nitList);

    // Aggregate by date if grouped by NIT
    const groupedByDate = {};
    relevantData.forEach(record => {
      const date = record.RecordDate;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {
          RecordDate: date,
          NegroCount: 0,
          RojoCount: 0,
          TotalReferences: 0
        };
      }
      groupedByDate[date].NegroCount += record.NegroCount || 0;
      groupedByDate[date].RojoCount += record.RojoCount || 0;
      groupedByDate[date].TotalReferences += record.TotalReferences || 0;
    });

    const sortedData = Object.values(groupedByDate)
      .sort((a, b) => a.RecordDate.localeCompare(b.RecordDate));

    // Calculate trend for forecast (simple linear regression)
    if (sortedData.length < 2) return sortedData;

    const criticalCount = sortedData.map(d => d.NegroCount + d.RojoCount);
    const n = criticalCount.length;

    // Calculate trend using linear regression
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    criticalCount.forEach((y, x) => {
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Add forecast data (extend 5 periods into the future)
    const forecastData = [...sortedData];
    const lastDate = new Date(sortedData[sortedData.length - 1].RecordDate);

    for (let i = 1; i <= 5; i++) {
      const forecastDate = new Date(lastDate);
      forecastDate.setDate(forecastDate.getDate() + i);

      const forecastCritical = Math.round(
        Math.max(0, slope * (n + i - 1) + intercept)
      );

      forecastData.push({
        RecordDate: forecastDate.toISOString().split('T')[0],
        NegroCount: forecastCritical,
        RojoCount: 0,
        TotalReferences: sortedData[sortedData.length - 1].TotalReferences,
        isForecast: true,
        slope: slope,
        intercept: intercept
      });
    }

    return forecastData;
  }, [data, nitList]);

  // Calculate trend direction and description
  const trendInfo = React.useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const firstForecast = chartData.find(d => d.isForecast);

    if (!firstForecast) return null;

    if (firstForecast.slope > 0.1) {
      return {
        direction: 'increasing',
        label: 'Tendencia Creciente',
        color: '#dc2626',
        description: 'El número de SKUs críticos está aumentando. Se requiere acción preventiva.',
        icon: '📈'
      };
    } else if (firstForecast.slope < -0.1) {
      return {
        direction: 'decreasing',
        label: 'Tendencia Decreciente',
        color: '#16a34a',
        description: 'El número de SKUs críticos está disminuyendo. Buen pronóstico.',
        icon: '📉'
      };
    } else {
      return {
        direction: 'stable',
        label: 'Tendencia Estable',
        color: '#6b7280',
        description: 'El número de SKUs críticos se mantiene constante. Monitorear continuamente.',
        icon: '➡️'
      };
    }
  }, [chartData]);

  // Responsive settings
  const chartHeight = isXs ? 220 : isSm ? 300 : 380;
  const marginValue = isXs ? 8 : 20;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const isForecast = payload[0].payload.isForecast;
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        fontSize: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '150px'
      }}>
        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1f2937' }}>
          {formatDate(label)}
          {isForecast && (
            <span style={{
              marginLeft: 6,
              padding: '2px 6px',
              backgroundColor: '#fef3c7',
              borderRadius: '4px',
              fontSize: '10px',
              color: '#92400e',
              fontWeight: 500
            }}>
              PRONÓSTICO
            </span>
          )}
        </p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: '4px 0', color: entry.color || '#666', fontWeight: 500 }}>
            {entry.name}: {entry.value} SKUs
          </p>
        ))}
      </div>
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          height: '100%',
          borderRadius: '12px',
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: chartHeight,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: isXs ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Datos insuficientes
          </div>
          <div style={{ fontSize: isXs ? 12 : 13 }}>
            Se requieren al menos 2 días de datos para generar el pronóstico
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}
      bodyStyle={{ padding: isXs ? 16 : 20 }}
    >
      {/* Chart */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart
            data={chartData}
            margin={{ top: marginValue, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="RecordDate"
              tickFormatter={formatDate}
              angle={isXs ? -90 : -45}
              textAnchor="end"
              height={isXs ? 50 : 80}
              style={{ fontSize: isXs ? 9 : 11, color: '#6b7280' }}
              interval={isXs ? 1 : 0}
            />
            <YAxis
              style={{ fontSize: isXs ? 9 : 11, color: '#6b7280' }}
              label={{ value: 'SKUs Críticos', angle: -90, position: 'insideLeft', style: { fontSize: '11px' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: isXs ? 10 : 12, paddingTop: 8 }}
              iconType="circle"
            />
            <Area
              type="monotone"
              dataKey="NegroCount"
              name={isXs ? 'Críticos' : 'SKUs Críticos (≤60% inventario)'}
              stroke="#dc2626"
              fill="url(#criticalGradient)"
              strokeWidth={2}
              strokeDasharray={(entry) => entry.isForecast ? '6 4' : '0 0'}
              connectNulls={false}
            />
            <defs>
              <linearGradient id="criticalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#dc2626" stopOpacity={0.4}/>
                <stop offset="100%" stopColor="#dc2626" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Trend Indicator */}
      {trendInfo && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: trendInfo.color + '10',
          borderRadius: '10px',
          borderLeft: `4px solid ${trendInfo.color}`,
          marginBottom: 16
        }}>
          <Space size={12}>
            <span style={{ fontSize: 20 }}>{trendInfo.icon}</span>
            <div>
              <Text strong style={{ color: trendInfo.color, fontSize: isXs ? 13 : 14 }}>
                {trendInfo.label}
              </Text>
              <Paragraph
                style={{
                  margin: 0,
                  fontSize: isXs ? 11 : 12,
                  color: '#6b7280',
                  marginBottom: 0
                }}
              >
                {trendInfo.description}
              </Paragraph>
            </div>
          </Space>
        </div>
      )}

      {/* Calculation Legend */}
      <div style={{
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        padding: isXs ? 14 : 16,
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            {'Como se calcula'}
          </Text>
        </div>

        <Paragraph style={{
          margin: '0 0 12px 0',
          fontSize: isXs ? 11 : 12,
          color: '#6b7280',
        }}>
          Se cuentan los productos con inventario critico (Negro + Rojo) por dia. Basado en la tendencia de esos datos, se proyecta lo que podria pasar en los proximos 5 dias.
        </Paragraph>

        <div style={{
          padding: '8px 12px',
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          border: '1px dashed #d1d5db',
          fontSize: isXs ? 10 : 11,
          color: '#4b5563'
        }}>
          <Text style={{ fontSize: isXs ? 10 : 11 }}>
            <strong>Area sombreada con linea punteada</strong> = datos proyectados (no reales)
          </Text>
        </div>

        {/* Recommendation based on trend */}
        {trendInfo && (
          <div style={{
            marginTop: 12,
            padding: '10px 12px',
            backgroundColor: trendInfo.direction === 'increasing' ? '#fff7ed' : trendInfo.direction === 'decreasing' ? '#f0fdf4' : '#f9fafb',
            borderRadius: '8px',
            border: `1px solid ${trendInfo.direction === 'increasing' ? '#fed7aa' : trendInfo.direction === 'decreasing' ? '#bbf7d0' : '#e5e7eb'}`
          }}>
            <Text strong style={{ fontSize: isXs ? 11 : 12, color: '#1f2937' }}>
              {'Que hacer: '}
            </Text>
            <Text style={{ fontSize: isXs ? 11 : 12, color: '#6b7280' }}>
              {trendInfo.direction === 'increasing'
                ? 'Anticipa pedidos a proveedores. Los productos criticos estan aumentando y podrias tener desabastecimiento pronto.'
                : trendInfo.direction === 'decreasing'
                  ? 'La situacion esta mejorando. Las acciones de reposicion estan funcionando.'
                  : 'La situacion se mantiene estable. Continua monitoreando.'
              }
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
}

export default StockoutForecastChart;
